/**
 * FirmaPortalKisitlariSekmesi.jsx — Firma Detay › Portal Kısıtları (backlog #417, 2026-10-14; tasarım #409 D9)
 *
 * SAKA personelinin bir firmanın portalındaki BİR MODÜLÜ düşürmesi: modül, seviye (NONE / READ / WRITE — kısıt yalnızca
 * düşürür), kapsam (firmanın TÜM fuarları ya da yalnızca bir fuar) ve personelin yazdığı GEREKÇE; gerekçe firmaya,
 * reddedilen işlemin yanında gösterilir ("SAKA bu modülü firmanız için kısıtladı: ..."). Etkin ve sonlandırılmış kısıtlar listelenir
 * (kim koydu, ne zaman, kim kaldırdı); sonlandırılan satır silinmez, geçmiş olarak kalır. Aynı firma + kapsam + modül için tek etkin kısıt olur.
 * Ekleme / kaldırma portal.kullanici_rol.manage ister (Admin, kök); diğer personel listeyi salt okunur görür.
 *
 * Veri: services/portalRbacService.js → /portal-rbac/firma/{firmaId}/kisitlar.
 */
import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Table, Badge, Button, Input, Label, FormGroup, FormText, Alert, Spinner, Row, Col,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { getFirmaKisitlari, addKisit, updateKisit, PORTAL_IZNI } from "../../../../services/portalRbacService";
import { useYetki } from "../../../../services/rbacService";

const KISIT_SEVIYELERI = ["NONE", "READ", "WRITE"];
const SEVIYE_RENK = { NONE: "danger", READ: "info", WRITE: "primary", MANAGE: "success" };

export default function FirmaPortalKisitlariSekmesi({ firmaId, fuarId }) {
  const { t } = useTranslation("ayarlar");
  const duzenleyebilir = useYetki(PORTAL_IZNI.KULLANICI_ROL) === true;
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);
  const [form, setForm] = useState({ modul: "", seviye: "NONE", kapsam: "HEPSI", gerekce: "" });
  const [calisiyor, setCalisiyor] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [kaldirilan, setKaldirilan] = useState(null);
  const [yenile, setYenile] = useState(0);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect); yazmadan sonra yeniden okuma `yenile` sayacıyla.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const data = await getFirmaKisitlari(firmaId);
        if (ignore) return;
        setVeri(data);
        setHata(null);
        setForm((f) => (f.modul ? f : { ...f, modul: data.moduller?.[0] ?? "" }));
      } catch (e) {
        if (!ignore) setHata(e.message || t("portalKisitlari.okunamadi"));
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaId, yenile]);

  const ekle = async () => {
    setCalisiyor(true);
    setIslemHatasi(null);
    try {
      await addKisit(firmaId, {
        modul: form.modul, seviye: form.seviye, fuarId: form.kapsam === "FUAR" ? fuarId : null, gerekce: form.gerekce.trim(),
      });
      setBilgi(t("portalKisitlari.eklendi"));
      setForm((f) => ({ ...f, gerekce: "" }));
      setYenile((x) => x + 1);
    } catch (e) {
      setIslemHatasi(e.message || t("portalKisitlari.kaydedilemedi"));
    } finally {
      setCalisiyor(false);
    }
  };

  const kaldir = async () => {
    setCalisiyor(true);
    setIslemHatasi(null);
    try {
      await updateKisit(firmaId, kaldirilan.id, { bitir: true });
      setKaldirilan(null);
      setBilgi(t("portalKisitlari.kaldirildi"));
      setYenile((x) => x + 1);
    } catch (e) {
      setIslemHatasi(e.message || t("portalKisitlari.kaydedilemedi"));
    } finally {
      setCalisiyor(false);
    }
  };

  const kapsam = (k) => (k.fuarId == null ? t("portalKisitlari.hepsi") : t("portalKisitlari.fuar", { id: k.fuarId }));
  const etkin = (veri?.kisitlar ?? []).filter((k) => k.aktif);
  const gecmis = (veri?.kisitlar ?? []).filter((k) => !k.aktif);
  const gecerli = form.modul && form.gerekce.trim().length > 0 && form.gerekce.length <= 500;

  const satir = (k, sonlandirildi) => (
    <tr key={k.id} data-kisit={k.id} className={sonlandirildi ? "text-muted" : undefined}>
      <td>{t(`portalArayuzleri.modul.${k.modul}`)}</td>
      <td><Badge color={SEVIYE_RENK[k.seviye] || "secondary"}>{t(`portalArayuzleri.seviye.${k.seviye}`)}</Badge></td>
      <td className="small">{kapsam(k)}</td>
      <td className="small" style={{ maxWidth: 320 }}>{k.gerekce}</td>
      <td className="small">
        {k.olusturanAdi || `#${k.olusturanUserId ?? "?"}`}<br />
        <span className="text-muted">{(k.olusturmaTarihi || "").slice(0, 10)}</span>
      </td>
      <td className="small">
        {sonlandirildi
          ? <>{t("portalKisitlari.sonlandirildi")}<br /><span className="text-muted">{k.bitirenAdi || `#${k.bitirenUserId ?? "?"}`} · {(k.bitisTarihi || "").slice(0, 10)}</span></>
          : duzenleyebilir && (
            <Button color="danger" outline size="sm" data-testid={`kisit-kaldir-${k.id}`} onClick={() => { setIslemHatasi(null); setKaldirilan(k); }}>
              {t("portalKisitlari.kaldir")}
            </Button>
          )}
      </td>
    </tr>
  );

  return (
    <Card>
      <CardBody>
        <h5 className="mb-1">{t("portalKisitlari.baslik")}</h5>
        <div className="text-muted small mb-3">{t("portalKisitlari.aciklama")}</div>

        {!duzenleyebilir && <Alert color="info" className="small" data-testid="portal-kisit-salt">{t("portalKisitlari.saltOkunur")}</Alert>}
        {hata && <Alert color="danger">{hata}</Alert>}
        {bilgi && <Alert color="success" toggle={() => setBilgi(null)}>{bilgi}</Alert>}
        {!veri && !hata && <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>}

        {veri && (
          <>
            <div className="table-responsive">
              <Table size="sm" className="align-middle" data-testid="portal-kisit-tablo">
                <thead className="table-light">
                  <tr>
                    <th>{t("portalKisitlari.modul")}</th>
                    <th>{t("portalKisitlari.seviye")}</th>
                    <th>{t("portalKisitlari.kapsam")}</th>
                    <th>{t("portalKisitlari.gerekce")}</th>
                    <th>{t("portalKisitlari.koyan")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {etkin.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-3">{t("portalKisitlari.bos")}</td></tr>}
                  {etkin.map((k) => satir(k, false))}
                </tbody>
              </Table>
            </div>

            {duzenleyebilir && (
              <div className="border rounded p-3 mb-3" data-testid="portal-kisit-form">
                <h6 className="mb-2">{t("portalKisitlari.yeniKisit")}</h6>
                {islemHatasi && !kaldirilan && <Alert color="danger" className="small">{islemHatasi}</Alert>}
                <Row className="g-2">
                  <Col md={4}>
                    <Label className="small mb-1">{t("portalKisitlari.modul")}</Label>
                    <Input type="select" bsSize="sm" value={form.modul} data-testid="kisit-modul" onChange={(e) => setForm((f) => ({ ...f, modul: e.target.value }))}>
                      {(veri.moduller ?? []).map((m) => <option key={m} value={m}>{t(`portalArayuzleri.modul.${m}`)}</option>)}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Label className="small mb-1">{t("portalKisitlari.seviye")}</Label>
                    <Input type="select" bsSize="sm" value={form.seviye} data-testid="kisit-seviye" onChange={(e) => setForm((f) => ({ ...f, seviye: e.target.value }))}>
                      {KISIT_SEVIYELERI.map((s) => <option key={s} value={s}>{t(`portalArayuzleri.seviye.${s}`)}</option>)}
                    </Input>
                    <FormText>{t("portalKisitlari.yalnizcaDusurur")}</FormText>
                  </Col>
                  <Col md={4}>
                    <Label className="small mb-1">{t("portalKisitlari.kapsam")}</Label>
                    <Input type="select" bsSize="sm" value={form.kapsam} data-testid="kisit-kapsam" onChange={(e) => setForm((f) => ({ ...f, kapsam: e.target.value }))}>
                      <option value="HEPSI">{t("portalKisitlari.hepsi")}</option>
                      <option value="FUAR" disabled={!fuarId}>{t("portalKisitlari.fuar", { id: fuarId ?? "?" })}</option>
                    </Input>
                  </Col>
                  <Col xs={12}>
                    <FormGroup className="mb-1">
                      <Label className="small mb-1">{t("portalKisitlari.gerekce")}</Label>
                      <Input type="textarea" rows={2} maxLength={500} value={form.gerekce} data-testid="kisit-gerekce"
                        onChange={(e) => setForm((f) => ({ ...f, gerekce: e.target.value }))} />
                      <FormText>{t("portalKisitlari.gerekceIpucu")}</FormText>
                    </FormGroup>
                  </Col>
                </Row>
                <Button color="primary" size="sm" disabled={!gecerli || calisiyor} onClick={ekle} data-testid="kisit-ekle">
                  {calisiyor && !kaldirilan ? <Spinner size="sm" /> : t("portalKisitlari.ekle")}
                </Button>
              </div>
            )}

            {gecmis.length > 0 && (
              <>
                <h6 className="mb-2">{t("portalKisitlari.sonlananlar")}</h6>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle" data-testid="portal-kisit-gecmis">
                    <tbody>{gecmis.map((k) => satir(k, true))}</tbody>
                  </Table>
                </div>
              </>
            )}
          </>
        )}
      </CardBody>

      <Modal isOpen={Boolean(kaldirilan)} toggle={() => setKaldirilan(null)}>
        <ModalHeader toggle={() => setKaldirilan(null)}>{t("portalKisitlari.kaldir")}</ModalHeader>
        <ModalBody>
          {islemHatasi && <Alert color="danger" className="small">{islemHatasi}</Alert>}
          {kaldirilan && t("portalKisitlari.kaldirOnay", {
            modul: t(`portalArayuzleri.modul.${kaldirilan.modul}`), seviye: t(`portalArayuzleri.seviye.${kaldirilan.seviye}`),
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setKaldirilan(null)}>{t("portalKisitlari.vazgec")}</Button>
          <Button color="danger" disabled={calisiyor} onClick={kaldir} data-testid="kisit-kaldir-onayla">{t("portalKisitlari.kaldir")}</Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
